import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { useTeacherContext } from "@/lib/teacher-context";
import { supabase } from "@/integrations/supabase/client";
import { videoService } from "@/lib/video/service";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Video, Calendar, Plus, Radio, Users } from "lucide-react";

export const Route = createFileRoute("/teacher/live")({
  ssr: false,
  beforeLoad: () => requireRole("teacher"),
  head: () => ({ meta: [{ title: "Live Classroom — MAKAI" }] }),
  component: TeacherLivePage,
});

type LiveLesson = {
  id: string; title: string; description: string | null;
  scheduled_start: string; scheduled_end: string;
  status: string; stream_id: string; subject_id: string | null;
  provider: string; room_name: string | null;
};

function TeacherLivePage() {
  const ctx = useTeacherContext();
  const [lessons, setLessons] = useState<LiveLesson[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "",
    stream_id: "", subject_id: "",
    scheduled_start: new Date(Date.now() + 30 * 60_000).toISOString().slice(0, 16),
    duration_min: 45,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!ctx.staffId) return;
    (async () => {
      const { data } = await supabase
        .from("live_lessons")
        .select("id,title,description,scheduled_start,scheduled_end,status,stream_id,subject_id,provider,room_name")
        .eq("teacher_id", ctx.staffId!)
        .order("scheduled_start", { ascending: false })
        .limit(50);
      setLessons((data ?? []) as LiveLesson[]);
    })();
  }, [ctx.staffId]);

  useEffect(() => {
    if (!form.stream_id && ctx.streams.length) setForm((f) => ({ ...f, stream_id: ctx.streams[0].id }));
  }, [ctx.streams, form.stream_id]);

  async function schedule() {
    if (!ctx.staffId || !ctx.schoolId || !form.stream_id || !form.title) {
      toast.error("Title and class are required"); return;
    }
    setSaving(true);
    const start = new Date(form.scheduled_start);
    const end = new Date(start.getTime() + form.duration_min * 60_000);
    const room = videoService.createRoom(ctx.schoolId);
    const { data, error } = await supabase.from("live_lessons").insert({
      school_id: ctx.schoolId, teacher_id: ctx.staffId,
      stream_id: form.stream_id,
      subject_id: form.subject_id || null,
      title: form.title, description: form.description || null,
      scheduled_start: start.toISOString(), scheduled_end: end.toISOString(),
      status: "scheduled",
      provider: room.provider,
      room_name: room.roomName,
      room_url: room.roomUrl,
    }).select().single();
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Live lesson scheduled");
    setLessons((l) => [data as LiveLesson, ...l]);
    setShowForm(false);
    setForm((f) => ({ ...f, title: "", description: "" }));
  }

  async function cancelLesson(id: string) {
    const { error } = await supabase.from("live_lessons").update({ status: "cancelled" }).eq("id", id);
    if (error) toast.error(error.message);
    else { toast.success("Cancelled"); setLessons((arr) => arr.map((l) => l.id === id ? { ...l, status: "cancelled" } : l)); }
  }

  return (
    <AppShell role="teacher" title="Live Classroom" subtitle="Schedule and host video lessons"
      action={<Button onClick={() => setShowForm((s) => !s)} className="gap-2"><Plus className="h-4 w-4" /> New lesson</Button>}>

      <div className="mb-4 rounded-xl border border-gold/40 bg-gold/10 p-3 text-xs text-gold-foreground">
        Video engine: <strong>{videoService.defaultProvider().label}</strong>
        {!videoService.defaultProvider().capabilities.cloudRecording &&
          " — cloud recording isn't available on this engine, so hosts record locally and publish afterwards."}{" "}
        Makai features run through the shared video service, so the engine can be swapped later without changing lessons.
      </div>

      {showForm && (
        <Card className="mb-6 p-5">
          <h2 className="mb-4 font-display text-lg font-bold">Schedule a live lesson</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <Label>Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Fractions revision" />
            </div>
            <div>
              <Label>Class / Stream</Label>
              <select className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.stream_id} onChange={(e) => setForm({ ...form, stream_id: e.target.value })}>
                {ctx.streams.map((s) => <option key={s.id} value={s.id}>{s.class_name} · {s.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Subject (optional)</Label>
              <select className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.subject_id} onChange={(e) => setForm({ ...form, subject_id: e.target.value })}>
                <option value="">—</option>
                {ctx.subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Start</Label>
              <Input type="datetime-local" value={form.scheduled_start} onChange={(e) => setForm({ ...form, scheduled_start: e.target.value })} />
            </div>
            <div>
              <Label>Duration (minutes)</Label>
              <Input type="number" min={10} max={240} value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: Number(e.target.value) })} />
            </div>
            <div className="md:col-span-2">
              <Label>Description</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <Button onClick={schedule} disabled={saving}>{saving ? "Saving..." : "Schedule"}</Button>
            <Button variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
          </div>
        </Card>
      )}

      <div className="space-y-3">
        {lessons.length === 0 ? (
          <Card className="grid place-items-center p-10 text-center text-sm text-muted-foreground">
            <Video className="mb-3 h-10 w-10 opacity-40" />
            No live lessons yet. Schedule your first one above.
          </Card>
        ) : lessons.map((l) => {
          const start = new Date(l.scheduled_start);
          const end = new Date(l.scheduled_end);
          const now = Date.now();
          const isLive = l.status !== "cancelled" && now >= start.getTime() - 10 * 60_000 && now <= end.getTime() + 30 * 60_000;
          return (
            <Card key={l.id} className="grid gap-3 p-4 md:grid-cols-[1fr_auto] md:items-center">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="truncate font-semibold">{l.title}</h3>
                  {isLive && <span className="inline-flex items-center gap-1 rounded-full bg-destructive/15 px-2 py-0.5 text-[11px] font-semibold text-destructive"><Radio className="h-3 w-3 animate-pulse" /> LIVE WINDOW</span>}
                  {l.status === "cancelled" && <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">Cancelled</span>}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {start.toLocaleString()} – {end.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                </div>
                {l.description && <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{l.description}</p>}
              </div>
              <div className="flex flex-wrap gap-2">
                {l.status !== "cancelled" && (
                  <Button asChild size="sm" className="gap-1"><Link to="/teacher/live/$id" params={{ id: l.id }}><Video className="h-4 w-4" /> Host</Link></Button>
                )}
                <Button asChild size="sm" variant="outline" className="gap-1"><Link to="/teacher/live/$id" params={{ id: l.id }}><Users className="h-4 w-4" /> Attendance</Link></Button>
                {l.status !== "cancelled" && <Button size="sm" variant="ghost" onClick={() => cancelLesson(l.id)}>Cancel</Button>}
              </div>
            </Card>
          );
        })}
      </div>
    </AppShell>
  );
}
